export interface IPluralizeDefinitions {
	[key: number]: string;
	empty?: string;
}

/**
 * Joins list of rest parameter strings with space characters.
 * Will ignore NULL and UNDEFINED.
 * @param strings strings to join together
 * @returns all the given strings join together with space characters
 */
export const joiner = (...strings: string[]): string => strings.filter(Boolean).join(" ");

/**
 * Truncates a string and concats an ellipsis if the length
 * of the string is greater than the max length
 * @param text string to truncate
 * @param maxLength maximum allowed length
 * @returns text or text with ellipsis
 */
export const truncateStringWithEllipsis = (text: string, maxLength: number = 60) => {
	if (!text)
		return "";

	if (text.length <= maxLength)
		return text;

	return text.substring(0, maxLength) + "...";
};


/**
 * Truncates long file name to given length with extension preserved
 * @param fileName string to truncate
 * @param maxLength maximum allowed length
 * @returns text or text with ellipsis
 */
export const truncateFileNamePreservingExtension = (fileName: string, maxLength: number = 60) => {
	if (!fileName)
		return "";

	if (fileName.length <= maxLength)
		return fileName;

	// find extension
	let index = fileName.lastIndexOf('.');

	// no extension, cut right to length
	if (index < 0) {
		return fileName.substring(0, maxLength) + "...";
	}

	// try shift cutting position to keep some text immediately before the extension
	if (index > 12) {
		index = index - 6;
	}
	
	// with extension, cut in the middle
	return fileName.substring(0, Math.min(maxLength - (fileName.length - index), index - 1)) + "..." + fileName.substring(index);
};

/**
 * Truncates the text at the specified position and appends the suffix. When suffixLengthMatters, maxLength applies to the final string.
 * Falls back to a hard truncation at maxLength if it's not possible to truncate on a word.
 * 
 * Samples:
 * 	truncateOnWord("This isatest!", 5, " ", "...", true)
 *  = "Th..."
 * 
 * 	truncateOnWord("This isatest!", 5, " ", "...", true)
 *  = "This"
 * 
 * 	truncateOnWord("This isatest!", 5, " ", "...", false)
 *  = "This..."
 * 
 * @param text The text to truncate
 * @param maxLength Max length of the truncated string
 * @param separator Separators for words to consider. Usually " ".
 * @param suffix Any suffix to add to indicate truncation
 * @param suffixLengthMatters The suffix counts towards the max length
  */
export function truncateOnWord(text: string, maxLength: number, separator: string = " ", suffix: string = "...", suffixLengthMatters: boolean = true) {
	const s = text || "";
	const max = maxLength || 0;

	if (max <= 0 || s.length <= max) {
		return s;
	}

	const suffixedMax = suffixLengthMatters ? max - suffix.length : max;
	if (suffixedMax < 0) {
		return s.slice(0, max);
	}

	const trunc = (length: number) => {
		const index = s.lastIndexOf(separator, length);
		if (index > -1) {
			return s.slice(0, index) + suffix;
		}

		return undefined;
	};

	let v = trunc(max);
	if (v && v.length > max) {
		v = trunc(suffixedMax);
	}

	//Fallback to truncate with ellipsis with word break if needed.
	return v || s.slice(0, suffixedMax) + suffix;
}

/**
 * Generates the correct pluralized string based on a definition and a given count.
 * If the given count isn't found, the next definition in the approaching zero direction will be used.
 * If the count is empty then the "empty" definition will be used.
 * @example 10 will move down to 9, -5 will move up to -4.
 * @param count the value for the pluralized definition to search for
 * @param list list of pluralized definitions
 */
export const pluralize = (count: number, list: IPluralizeDefinitions): string => {

	if (count == null)
		return list.empty || null;

	if (list[count] !== undefined)
		return list[count];

	const keys = Object.getOwnPropertyNames(list).map(x => +x).filter(x => !isNaN(x));
	const ordered = [count, ...keys].sort((a, b) => a - b);
	const keyIndex = ordered.indexOf(count) - (count >= 0 ? 1 : -1);

	return keyIndex >= 0 ? list[ordered[keyIndex]] : null;
}

type DateRangeX = {
	startDate?: string;
	startDateX?: string;
	startDateXDeletedOnDate?: Date;
	stopDate?: string;
	stopDateX?: string;
	stopDateXDeletedOnDate?: Date;
};

export const divideDates = (obj: DateRangeX, divider = "-"): string => {
	if (obj.startDate && obj.stopDate)
		return `${obj.startDate} ${divider} ${obj.stopDate}`;

	if (obj.startDateX && obj.stopDateX && !obj.startDateXDeletedOnDate && !obj.stopDateXDeletedOnDate)
		return `${obj.startDateX} ${divider} ${obj.stopDateX}`;

	if (obj.startDate && obj.stopDateX && !obj.stopDateXDeletedOnDate)
		return `${obj.startDate} ${divider} ${obj.stopDateX}`;

	if (obj.startDateX && obj.stopDate && !obj.startDateXDeletedOnDate)
		return `${obj.startDateX} ${divider} ${obj.stopDate}`;

	if (obj.startDate)
		return obj.startDate;

	if (obj.startDateX && !obj.startDateXDeletedOnDate)
		return obj.startDateX;

	return "";
};

export const capitalizeFirstLetter = (value: string, remainderToLower: boolean = false) =>
	value.charAt(0).toUpperCase() + (remainderToLower ? value.slice(1).toLowerCase() : value.slice(1));

const isUpper = (c: string) => c != null && c.length === 1 && c === c.toUpperCase() && c !== c.toLowerCase();
const isLower = (c: string) => c != null && c.length === 1 && c === c.toLowerCase() && c !== c.toUpperCase();
const isDigit = (c: string) => c != null && c.length === 1 && "1234567890".indexOf(c) >= 0;

export const splitCamelCase = (source: string, trim: boolean = true, capitalizeFirst = true): string => {
	let sb = "";
	let delimiter = " ";
	let text = source || "";

	for (let i = 0; i < text.length; i++) {
		let c = text[i];

		//Insert delimiter if needed
		if (i > 0) {
			let lastIsLower = isLower(text[i - 1]);
			if (isUpper(c)) {
				let lastIsDigit = isDigit(text[i - 1]);
				if (i + 1 < text.length) {
					let nextIsUpper = isUpper(text[i + 1]);
					if (!nextIsUpper)
						sb += delimiter;

					else if (lastIsLower || lastIsDigit)
						sb += delimiter;
				} else {
					if (lastIsLower)
						sb += delimiter;
				}
			} else if (isDigit(c)) {
				let lastIsUpper = isUpper(text[i - 1]);
				if (i + 1 < text.length) {
					let nextIsDigit = isDigit(text[i + 1]);
					if (!nextIsDigit)
						sb += delimiter;

					else if (lastIsLower || lastIsUpper)
						sb += delimiter;
				} else {
					if (lastIsLower)
						sb += delimiter;
				}
			}
		}

		sb += c;
	}

	if (capitalizeFirst) {
		sb = capitalizeFirstLetter(sb);
	}
	return trim ? sb.trim() : sb;
};


/**
 * Transform base64 string to byte array
 * @param data 
 */
 export const base64ToArrayBuffer = (data) => {
	let binaryString = window.atob(data);
	let binaryLen = binaryString.length;
	let bytes = new Uint8Array(binaryLen);
	for (let i = 0; i < binaryLen; i++) {
		let ascii = binaryString.charCodeAt(i);
		bytes[i] = ascii;
	}
	return bytes;
}
